import {
  FlexTable,
  Link,
  Avatar,
  Icon,
  ModalTrigger,
  ButtonTooltip,
  FlexTableSortHeaderCell,
} from '@evertrue/et-components';
import Api from 'entities/helpers/api';
import colors from 'base/colors';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import InteractionAddBtn from 'apps/interactions/components/create/interaction-add-btn';
import CallNotesModal from './call-notes-modal';

const WIDTH_S = '75px';
const WIDTH_M = '150px';
const WIDTH_L = '275px';
const WIDTH_XL = '350px';

const CallsTable = ({ calls, loading, setCalls }) => {
  const emptyStateHeader = 'Call me, Maybe?';
  const emptyStateSubtext =
    'Your outbound phone calls will appear here. Once you make a call, you can find it here to review or follow up when needed.';

  const renderHeader = (header, key, tooltipMessage) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <FlexTableSortHeaderCell onClick={() => {}} children={header} />
      {tooltipMessage && (
        <ButtonTooltip trigger={<Icon icon="info" style={{ color: colors.gray500 }} />} contentWidth={150}>
          {tooltipMessage}
        </ButtonTooltip>
      )}
    </div>
  );

  const statusTooltipMessage = `
  Connected: a connection was made with the recipient's device, even if they did not answer.\r
  Not Connected: it was not possible to connect to the recipient's device. This may be because the dialed number is not in service, or because a technical issue prevented a connection.
  `;

  const updateCall = async (id, payload) => {
    try {
      const newCall = await Api.OUTREACH.CALL.put({
        urlArgs: { id },
        data: JSON.stringify(payload),
      });
      const newCalls = calls.map((call) => {
        if (call.id === id) {
          const contactCopy = { ...call.contact };
          newCall.contact = contactCopy;
          return newCall;
        } else {
          return call;
        }
      });
      EverTrue.Alert.success('Call updated successfully');
      setCalls(newCalls);
    } catch (error) {
      EverTrue.Alert.error('Error updating call notes:', error);
    }
  };

  const columns = [
    {
      key: 1,
      width: WIDTH_M,
      header: 'Time',
      accessor: ({ created_at }) => {
        const { formattedDate, formattedTime } = formatCallTime(created_at);
        return (
          <div className="calls-table--cell-content-inner">
            <div>{formattedDate}</div>
            <div>{formattedTime}</div>
          </div>
        );
      },
    },
    {
      key: 2,
      width: WIDTH_L,
      header: 'Constituent',
      accessor: ({ contact = {}, contact_id }) => {
        const name = contact?.contact_attributes?.name_full || '-';
        const avatar = contact?.profile_image?.url || '';
        return contact_id ? (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <Link title={`${name}'s Profile`} href={`/contact/${contact_id}`}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Avatar className="new-contact-card--avatar fs-exclude-data" name={name} url={avatar} width={40} />
                <div className="journeys-contact-card-name table-toolbar-amount fs-exclude-data">{name}</div>
              </div>
            </Link>
          </span>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      key: 3,
      width: WIDTH_S,
      header: renderHeader('Status', 'status', statusTooltipMessage),
      accessor: ({ status }) => {
        const statusColor = status === 'FAILED' ? 'red' : status === 'SUCCESS' ? '#28a745' : 'inherit';
        const statusText = status === 'FAILED' ? 'Not Connected' : status === 'SUCCESS' ? 'Connected' : status;
        return (
          <div className="calls-table--cell-content-inner">
            <span style={{ color: statusColor }}>{statusText}</span>
          </div>
        );
      },
    },
    {
      key: 4,
      width: WIDTH_S,
      header: 'Duration',
      accessor: ({ duration_in_seconds }) => {
        let duration = '-';
        if (duration_in_seconds) {
          const minutes = Math.floor(duration_in_seconds / 60);
          const seconds = duration_in_seconds % 60;
          if (minutes > 0) {
            duration = `${minutes} min ${seconds} seconds`;
          } else {
            duration = `${seconds} seconds`;
          }
        }
        return <div className="calls-table--cell-content-inner">{duration}</div>;
      },
    },
    {
      key: 5,
      width: WIDTH_XL,
      header: 'Call Notes',
      accessor: (call = {}) => {
        return (
          <div className="calls-table--notes-container">
            <div className="calls-table--notes-content" onClick={() => {}}>
              <ModalTrigger
                closeOnOutsideClick={true}
                buttonProps={{ type: 'simple' }}
                buttonText={
                  call.call_notes ? (
                    <span className="calls-table--notes-content">{call.call_notes}</span>
                  ) : (
                    <Icon icon="add" className="calls-table--add-button" />
                  )
                }
              >
                {({ closeModal }) => (
                  <CallNotesModal key={call.id} onClose={closeModal} updateCall={updateCall} call={call} />
                )}
              </ModalTrigger>
            </div>
          </div>
        );
      },
    },
    {
      key: 6,
      width: WIDTH_S,
      header: 'Interactions',
      accessor: (call = {}) => {
        const { call_notes, interaction_id, contact = {} } = call;
        return (
          <div className="calls-table--cell-content-inner">
            {interaction_id ? (
              <Link href={`/interaction/${interaction_id}`}>
                <Icon icon="check-circle" className="calls-table--interaction-button" />
              </Link>
            ) : (
              <InteractionAddBtn
                contact={contact}
                afterSubmit={(interaction) => {
                  if (call?.id) {
                    const payload = {
                      interaction_id: interaction.id,
                      call_notes: call.call_notes,
                    };
                    updateCall(call.id, payload);
                  }
                }}
                interaction={{
                  summary: `Call with ${contact?.contact_attributes?.name_full}`,
                  text: call_notes ? `Call Notes: \n${call_notes}` : '',
                }}
                buttonClass="calls-table--add-button"
                buttonType="simple"
                title="+"
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <FlexTable
      className="calls-table"
      caption="Calls"
      emptyStateMessageText={emptyStateHeader}
      emptyStateMessageSubtext={emptyStateSubtext}
      emptyStateIcon={'call'}
      emptyStateIconKit={'gt'}
      columns={columns}
      data={calls}
      loading={loading}
    />
  );
};

export default CallsTable;

CallsTable.propTypes = {
  calls: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  setCalls: PropTypes.func.isRequired,
};

/**
 * Formats a Unix timestamp into a human-readable date and time string
 * @param {number} timestamp - Unix timestamp in seconds
 * @returns {Object} Object containing formatted date and time strings
 * @property {string} formattedDate - Date in format "MMM D, YYYY" (e.g. "May 2, 2021")
 * @property {string} formattedTime - Time in format "H:MM am/pm TZ" (e.g. "8:00 pm EDT")
 */

export const formatCallTime = (timestamp) => {
  // Create a date from the timestamp (in UTC)
  const date = new Date(timestamp * 1000);

  // Format the date using browser's timezone
  const dateOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const formattedDate = date.toLocaleDateString('en-US', dateOptions);

  // Format the time using browser's timezone
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';

  // Get timezone abbreviation dynamically
  const timeZoneAbbr =
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  const formattedTime = `${hours % 12 || 12}:${minutes} ${ampm} ${timeZoneAbbr}`;

  return { formattedDate, formattedTime };
};
